/**
 * 前端数据缓存服务 - 提供内存缓存和本地存储缓存
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface CacheConfig {
  defaultTTL: number; // 默认缓存时间（毫秒）
  maxMemoryItems: number; // 内存缓存最大项目数
  enableLocalStorage: boolean; // 是否启用本地存储缓存
}

class CacheService {
  private memoryCache = new Map<string, CacheItem<any>>();
  private config: CacheConfig = {
    defaultTTL: 5 * 60 * 1000, // 5分钟
    maxMemoryItems: 100,
    enableLocalStorage: true
  };

  /**
   * 设置缓存配置
   */
  configure(config: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * 设置缓存数据
   */
  set<T>(key: string, data: T, ttl?: number): void {
    const expiry = Date.now() + (ttl || this.config.defaultTTL);
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry
    };

    // 内存缓存
    this.memoryCache.set(key, cacheItem);
    this.cleanupMemoryCache();

    // 本地存储缓存
    if (this.config.enableLocalStorage) {
      try {
        localStorage.setItem(`cache_${key}`, JSON.stringify(cacheItem));
      } catch (error) {
        console.warn('本地存储缓存失败:', error);
      }
    }
  }

  /**
   * 获取缓存数据
   */
  get<T>(key: string): T | null {
    // 先检查内存缓存
    const memoryItem = this.memoryCache.get(key);
    if (memoryItem && memoryItem.expiry > Date.now()) {
      return memoryItem.data;
    }

    // 检查本地存储缓存
    if (this.config.enableLocalStorage) {
      try {
        const localItem = localStorage.getItem(`cache_${key}`);
        if (localItem) {
          const cacheItem: CacheItem<T> = JSON.parse(localItem);
          if (cacheItem.expiry > Date.now()) {
            // 重新加载到内存缓存
            this.memoryCache.set(key, cacheItem);
            return cacheItem.data;
          } else {
            // 过期数据，清理
            localStorage.removeItem(`cache_${key}`);
          }
        }
      } catch (error) {
        console.warn('读取本地存储缓存失败:', error);
      }
    }

    return null;
  }

  /**
   * 删除缓存
   */
  delete(key: string): void {
    this.memoryCache.delete(key);
    if (this.config.enableLocalStorage) {
      localStorage.removeItem(`cache_${key}`);
    }
  }

  /**
   * 清空所有缓存
   */
  clear(): void {
    this.memoryCache.clear();
    if (this.config.enableLocalStorage) {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('cache_')) {
          localStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * 检查缓存是否存在且未过期
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * 获取或设置缓存（如果不存在则执行获取函数）
   */
  async getOrSet<T>(
    key: string,
    fetchFn: () => Promise<T>,
    ttl?: number
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const data = await fetchFn();
    this.set(key, data, ttl);
    return data;
  }

  /**
   * 批量设置缓存
   */
  setMultiple<T>(items: Array<{ key: string; data: T; ttl?: number }>): void {
    items.forEach(item => {
      this.set(item.key, item.data, item.ttl);
    });
  }

  /**
   * 批量获取缓存
   */
  getMultiple<T>(keys: string[]): Array<{ key: string; data: T | null }> {
    return keys.map(key => ({
      key,
      data: this.get<T>(key)
    }));
  }

  /**
   * 清理过期的内存缓存
   */
  private cleanupMemoryCache(): void {
    const now = Date.now();
    const entries = Array.from(this.memoryCache.entries());

    // 删除过期项
    entries.forEach(([key, item]) => {
      if (item.expiry <= now) {
        this.memoryCache.delete(key);
      }
    });

    // 如果超过最大数量，删除最旧的项
    if (this.memoryCache.size > this.config.maxMemoryItems) {
      const sortedEntries = entries
        .filter(([, item]) => item.expiry > now)
        .sort((a, b) => a[1].timestamp - b[1].timestamp);

      const toDelete = sortedEntries.slice(0, this.memoryCache.size - this.config.maxMemoryItems);
      toDelete.forEach(([key]) => {
        this.memoryCache.delete(key);
      });
    }
  }

  /**
   * 获取缓存统计信息
   */
  getStats(): {
    memoryItems: number;
    localStorageItems: number;
    memorySize: number;
  } {
    let localStorageItems = 0;
    if (this.config.enableLocalStorage) {
      const keys = Object.keys(localStorage);
      localStorageItems = keys.filter(key => key.startsWith('cache_')).length;
    }

    return {
      memoryItems: this.memoryCache.size,
      localStorageItems,
      memorySize: JSON.stringify(Array.from(this.memoryCache.entries())).length
    };
  }

  /**
   * 预热缓存 - 预加载常用数据
   */
  async warmup(preloadFunctions: Array<{
    key: string;
    fetchFn: () => Promise<any>;
    ttl?: number;
  }>): Promise<void> {
    const promises = preloadFunctions.map(async ({ key, fetchFn, ttl }) => {
      try {
        if (!this.has(key)) {
          const data = await fetchFn();
          this.set(key, data, ttl);
        }
      } catch (error) {
        console.warn(`预热缓存失败 ${key}:`, error);
      }
    });

    await Promise.all(promises);
  }

  /**
   * 创建带缓存的API调用包装器
   */
  createCachedApiCall<T>(
    key: string,
    apiFn: () => Promise<T>,
    ttl?: number
  ): () => Promise<T> {
    return () => this.getOrSet(key, apiFn, ttl);
  }
}

// 创建全局缓存实例
export const cacheService = new CacheService();

// 导出缓存装饰器
export function cached(key: string, ttl?: number) {
  return function (target: any, propertyName: string, descriptor: PropertyDescriptor) {
    const method = descriptor.value;
    descriptor.value = async function (...args: any[]) {
      const cacheKey = `${key}_${JSON.stringify(args)}`;
      return cacheService.getOrSet(cacheKey, () => method.apply(this, args), ttl);
    };
  };
}

export default cacheService;